// Browser side of a practice call: microphone check, Retell web client (loaded on demand so the dialer bundle
// stays small), start/finish against /api/training. retell-client-js-sdk 3.x: the server creates the call (the
// Retell key never reaches the browser) and RetellWebClient joins it with the config the SDK's own WebCallSession
// would build (see retellConfig.ts). RetellWebClient is the 3.x "bring your own access token" client; the newer
// RetellClient creates calls itself from the browser, which would need a Retell key here.
import type { PracticeFinishResponse, PracticeStartResponseExt } from '../../../src/shared/api-ai';
import { ApiFailure, api, errorMessage } from '../api';
import { PRACTICE_TRANSPORTS, startCallConfig } from './retellConfig';

export type PracticePhase = 'idle' | 'mic' | 'starting' | 'connecting' | 'live' | 'scoring' | 'done' | 'error';

export interface PracticeHandlers {
  phase: (p: PracticePhase) => void;
  agentTalking: (talking: boolean) => void;
  error: (message: string) => void;
  scored: (r: PracticeFinishResponse) => void;
}

type RetellModule = typeof import('retell-client-js-sdk');
type Client = InstanceType<RetellModule['RetellWebClient']>;

/** Asks for the microphone once so the browser prompt appears before the call, then releases it. */
export async function checkMicrophone(): Promise<string | null> {
  if (!navigator.mediaDevices?.getUserMedia) return "This browser can't use a microphone on this page (it needs https). Try Chrome or Edge.";
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    return null;
  } catch (e) {
    const name = e instanceof DOMException ? e.name : '';
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'Microphone blocked. Click the lock icon in the address bar, allow the microphone, then try again.';
    if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No microphone found. Plug in your headset and try again.';
    if (name === 'NotReadableError') return 'The microphone is busy in another app (close other call apps) and try again.';
    return `Couldn't open the microphone (${name || 'unknown error'}).`;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class PracticeCall {
  private client: Client | null = null;
  private sessionId: number | null = null;
  private finished = false;
  private connected = false;
  private cancelled = false;
  private muted = false;
  private readonly onUnload = () => { try { this.client?.stopCall(); } catch { /* page is closing */ } };

  constructor(private readonly h: PracticeHandlers) {}

  get id(): number | null { return this.sessionId; }

  async start(personaId: number): Promise<PracticeStartResponseExt | null> {
    this.h.phase('mic');
    const micProblem = await checkMicrophone();
    if (micProblem) return this.fail(micProblem);

    this.h.phase('starting');
    let mod: RetellModule;
    try {
      mod = await import('retell-client-js-sdk');
    } catch {
      return this.fail("Couldn't load the practice call software. Reload the page and try again.");
    }

    let start: PracticeStartResponseExt;
    try {
      start = await api.post<PracticeStartResponseExt>('/api/training/practice/start', { personaId, transports: PRACTICE_TRANSPORTS });
    } catch (e) {
      return this.fail(errorMessage(e));
    }
    this.sessionId = start.sessionId;

    const client = new mod.RetellWebClient();
    this.client = client;
    client.on('call_started', () => {
      this.connected = true;
      if (this.cancelled) { client.stopCall(); return; } // hung up while connecting
      this.h.phase('live');
    });
    client.on('agent_start_talking', () => this.h.agentTalking(true));
    client.on('agent_stop_talking', () => this.h.agentTalking(false));
    client.on('call_ended', () => { this.h.agentTalking(false); void this.finish(); });
    client.on('error', (msg: unknown) => {
      const why = typeof msg === 'string' ? msg : 'connection error';
      if (!this.connected) {
        // Never connected: nothing to score. The SDK has already cleaned up.
        window.removeEventListener('beforeunload', this.onUnload);
        this.fail(`Couldn't connect the practice call (${why}). Check your internet connection and try again.`);
        return;
      }
      this.h.error(`The practice call dropped (${why}).`);
      try { client.stopCall(); } catch { /* already stopped */ }
    });

    this.h.phase('connecting');
    window.addEventListener('beforeunload', this.onUnload);
    try {
      await client.startCall(startCallConfig(start));
    } catch (e) {
      window.removeEventListener('beforeunload', this.onUnload);
      return this.fail(`Couldn't connect the practice call: ${errorMessage(e)}`);
    }
    return start;
  }

  toggleMute(): boolean {
    if (!this.client) return this.muted;
    this.muted = !this.muted;
    if (this.muted) this.client.mute(); else this.client.unmute();
    return this.muted;
  }

  /** Hang up. Scoring starts from the call_ended event (a call that never connected just ends). */
  hangUp(): void {
    if (!this.client) return;
    if (!this.connected) {
      this.cancelled = true;
      this.h.phase('idle');
    }
    try { this.client.stopCall(); } catch { /* already stopped */ }
  }

  /** Scores the session; retries while Retell is still finalising the call. Safe to call more than once. */
  async finish(): Promise<void> {
    if (this.finished || this.sessionId === null) return;
    this.finished = true;
    window.removeEventListener('beforeunload', this.onUnload);
    this.client = null;
    this.h.phase('scoring');
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const r = await api.post<PracticeFinishResponse>(`/api/training/practice/${this.sessionId}/finish`, {});
        this.h.scored(r);
        this.h.phase('done');
        return;
      } catch (e) {
        if (e instanceof ApiFailure && e.body.error === 'practice_not_ended' && attempt < 3) { await sleep(4000); continue; }
        this.h.error(`${errorMessage(e)} You can score it later from History.`);
        this.h.phase('error');
        return;
      }
    }
  }

  private fail(message: string): null {
    this.h.error(message);
    this.h.phase('error');
    return null;
  }
}
